import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Step } from '../../utils/types';

@Component({
  selector: 'app-stepper',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './stepper.html',
  styleUrl: './stepper.css'
})
export class Stepper implements AfterViewInit, OnChanges, OnDestroy {
  @Input() steps: Step[] = [];
  @Input() currentStep = 1;
  @Input() currentSubStep = 1;

  @ViewChildren('stepContainer') stepContainers!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('stepCircle') stepCircles!: QueryList<ElementRef<HTMLDivElement>>;

  connectorHeights: string[] = [];
  progressHeights: string[] = [];

  expandedStepIndex: number | null = null;

  private destroySubs: (() => void)[] = [];

  ngAfterViewInit(): void {
    // 1. Initial calculation
    this.scheduleUpdate();

    // 2. Recalculate when new step containers are added (e.g. *ngFor finishes)
    const sub = this.stepContainers.changes.subscribe(() => {
      this.scheduleUpdate();
    });
    this.destroySubs.push(() => sub.unsubscribe());

    // 3. Window resize
    const resizeHandler = () => this.scheduleUpdate();
    window.addEventListener('resize', resizeHandler);
    this.destroySubs.push(() => window.removeEventListener('resize', resizeHandler));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentStep']) {
      const stepIndex = this.currentStep - 1;
      const step = this.steps[stepIndex];

      // Always open the current step if it has substeps
      if (step?.subSteps?.length) {
        this.expandedStepIndex = stepIndex;
      }
    }

    if (changes['steps'] || changes['currentStep'] || changes['currentSubStep']) {
      this.scheduleUpdate();
    }
  }

  ngOnDestroy(): void {
    this.destroySubs.forEach(fn => fn());
  }

  toggleStepExpansion(index: number): void {
    this.expandedStepIndex = this.expandedStepIndex === index ? null : index;
    this.scheduleUpdate();
  }

  isExpanded(index: number): boolean {
    return this.expandedStepIndex === index;
  }

  private scheduleUpdate(): void {
    setTimeout(() => this.updateHeights(), 50); 
  }

  updateHeights(): void {
    const stepEls = this.stepContainers?.toArray() || [];

    if (stepEls.length === 0) {
      this.connectorHeights = [];
      this.progressHeights = [];
      return;
    }

    // === 1. Full connector (muted line) ===
    this.connectorHeights = stepEls.map((elRef, index) => {
      if (index === stepEls.length - 1) return '0px';
      const height = elRef.nativeElement.offsetHeight;
      return `${Math.max(height - 58, 24)}px`; // your original offset
    });

    // === 2. Blue progress overlay ===
    this.progressHeights = stepEls.map((elRef, index) => {
      // Past steps → fully filled
      if (index + 1 < this.currentStep) {
        return this.connectorHeights[index];
      }

      // Future steps → nothing
      if (index + 1 > this.currentStep) {
        return '0px';
      }

      // Current step
      const step = this.steps[index];
      const isExpanded = this.isExpanded(index);
      const hasSubsteps = !!step.subSteps?.length;

      if (!hasSubsteps || !isExpanded) {
        return '0px'; // closed or no substeps
      }

      const substepsEl = elRef.nativeElement.querySelector('.substeps') as HTMLElement;
      if (!substepsEl) return '0px';

      const substepEls = substepsEl.querySelectorAll('.substep') as NodeListOf<HTMLElement>;
      const totalSubs = substepEls.length;

      if (totalSubs === 0) return '0px';

      const completedSubs = Math.min(this.currentSubStep, totalSubs);

      if (completedSubs >= totalSubs) {
        return this.connectorHeights[index]; // all done → full line
      }

      const lastDoneEl = substepEls[completedSubs - 1];
      const connectorTop = 48; // top-12 = 48px
      const substepsTop = substepsEl.offsetTop;
      const subTop = lastDoneEl.offsetTop;
      const subHeight = lastDoneEl.offsetHeight;

      const fillHeight = substepsTop + subTop + subHeight - connectorTop;

      return `${Math.max(fillHeight, 0)}px`;
    });
  }

  getConnectorHeightFromDOM(index: number): string {
    return this.connectorHeights[index] || '24px';
  }

  getProgressHeight(index: number): string {
    return this.progressHeights[index] || '0px';
  }

  getOverallProgress(): number {
    if (!this.steps.length) return 0;

    const totalUnits = this.steps.reduce((acc, s) => acc + (s.subSteps?.length || 1), 0);
    let completed = 0;

    for (let i = 0; i < this.steps.length; i++) {
      if (i + 1 < this.currentStep) {
        completed += this.steps[i].subSteps?.length || 1;
      } else if (i + 1 === this.currentStep) {
        completed += Math.min(this.currentSubStep, this.steps[i].subSteps?.length || 1);
        break;
      }
    }

    return Math.min((completed / totalUnits) * 100, 100);
  }
}